import { useEffect, useRef, useState } from "react";
import { itemById } from "@zend/content";
import { api } from "../api/http.js";
import { requestScope } from "../api/scope.js";
import { getLocale, pick, useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { useBalances, type Balances } from "../state/balances.js";
import { errorText, useBase } from "../base/state.js";
import { Dialog } from "../ui/Dialog.js";
import { Button } from "../ui/Button.js";
import { StatIcon } from "../assets/gameIcons.js";
import { AssetIcon } from "../assets/Icon.js";
import { useStore } from "../store/state.js";
import { OfferCard, PurchaseFx, PurchaseSheet } from "../store/cards.js";
import type { StoreCard } from "../store/types.js";
import type { InventoryStack } from "../inventory/stock.js";
import { toast } from "../ui/Toast.js";
import "./fuel.css";

interface UseResult { collectionRef: string; inventory: InventoryStack[]; balances: Balances; resources: Record<string, number>; stamina: number; reused: boolean }

/** Fuel is the player-facing name of stamina; the ids stay `itm.stamina_*` (D65). */
export function isFuelItem(itemId: string): boolean {
  try {
    return itemById(itemId).kind === "stamina";
  } catch {
    return false;
  }
}

export function isFuelCard(card: StoreCard): boolean {
  return card.contents.items?.some((i) => isFuelItem(i.itemId)) ?? false;
}

/** Owned fuel items with a one-tap use; the inventory route stays the single authority for refills. */
export function OwnedFuel() {
  const t = useT();
  const inventory = useSession((s) => s.inventory);
  const owned = inventory.filter((i) => i.qty > 0 && isFuelItem(i.itemId));
  const [busy, setBusy] = useState<string | null>(null);
  const requests = useRef(new Map<string, string>());
  const use = async (itemId: string) => {
    if (busy) return;
    const { generation } = requestScope();
    const requestId = requests.current.get(itemId) ?? crypto.randomUUID();
    requests.current.set(itemId, requestId);
    setBusy(itemId);
    try {
      const data = await api<UseResult>("POST", "/api/inventory/use", { itemId, qty: 1, requestId });
      if (requestScope().generation !== generation) return;
      requests.current.delete(itemId);
      useSession.setState({ inventory: data.inventory });
      useBalances.getState().setBalances(data.balances, data.collectionRef);
      toast.success(t("inventory.used", { n: data.stamina.toLocaleString(getLocale()), resource: t("res.stamina") }));
      void useBase.getState().refresh();
    } catch (e) {
      if (requestScope().generation === generation) toast.error(errorText(e));
    } finally {
      if (requestScope().generation === generation) setBusy(null);
    }
  };
  if (owned.length === 0) return null;
  return (
    <section className="fuel-owned" data-testid="fuel-owned">
      <h3>{t("fuel.owned")}</h3>
      {owned.map((i) => (
        <div key={i.itemId} className="fuel-row" data-testid={`fuel-own-${i.itemId}`}>
          <AssetIcon id={i.itemId} size={44} />
          <span className="grow">
            <strong>{pick(itemById(i.itemId).name)}</strong>
            <small>×{i.qty}</small>
          </span>
          <Button variant="primary" loading={busy === i.itemId} disabled={busy !== null} onClick={() => void use(i.itemId)} data-testid={`fuel-use-${i.itemId}`}>
            {t("fuel.use")}
          </Button>
        </div>
      ))}
    </section>
  );
}

/** The fuel catalogue offers; buying goes through the shared purchase confirmation. */
export function FuelOffers({ cards }: { cards: StoreCard[] }) {
  const t = useT();
  return (
    <section className="fuel-offers" data-testid="fuel-offers">
      <h3>{t("fuel.buy")}</h3>
      <div className="offers fuel-offer-grid">
        {cards.map((card) => (
          <div key={card.key} data-testid={`fuel-offer-${card.key}`}>
            <OfferCard card={card} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** Opens from wherever fuel is spent: owned refills first, then the three catalogue sizes (D65). */
export function FuelSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const view = useStore((s) => s.view);
  const refresh = useStore((s) => s.refresh);
  const stamina = useBalances((s) => s.balances?.stamina ?? 0);
  const fx = useStore((s) => s.fx);
  useEffect(() => {
    if (!view) void refresh();
  }, [view, refresh]);
  // A purchase celebration means the ledger changed; the owned list reads the authoritative inventory again.
  useEffect(() => {
    if (!fx) return;
    const { generation } = requestScope();
    void api<{ balances: Balances; inventory: InventoryStack[] }>("GET", "/api/ledger/balances")
      .then((data) => {
        if (requestScope().generation !== generation) return;
        useSession.setState({ inventory: data.inventory });
        useBalances.getState().setBalances(data.balances);
      })
      .catch(() => undefined);
  }, [fx]);
  const fuel = (view?.resources ?? []).filter(isFuelCard);
  return (
    <Dialog title={t("fuel.title")} onClose={onClose} testId="fuel-sheet" surface="sheet">
      <p className="fuel-status" data-testid="fuel-status">
        <StatIcon stat="stamina" /> <strong>{stamina}</strong> <span className="hint">{t("fuel.hint")}</span>
      </p>
      <OwnedFuel />
      {fuel.length > 0 ? <FuelOffers cards={fuel} /> : <p className="hint">{t("common.loading")}</p>}
      <PurchaseSheet />
      <PurchaseFx />
    </Dialog>
  );
}
